import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { UrlConstantsService } from './url-constants.service';

export interface IAuthTokens {
  access: string;
  refresh?: string;
  accepted_terms?: boolean;
}

export interface IDecodedAccessToken {
  exp: number;
  jti: string;
  name: string;
  platform_admin: boolean;
  /** owner | admin | member. Only shapes the UI; the API enforces roles itself. */
  org_role?: 'owner' | 'admin' | 'member';
  token_type: string;
  user_id: number;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  public userName$: Observable<string>;
  public clientName$: Observable<string>;
  public decodedAccessToken$: Observable<IDecodedAccessToken>;
  private _afterLoginPath = 'after-login-link';
  private _accessToken = 'accessToken';
  private _refreshToken = 'refreshToken';
  private _acceptedTerms = 'acceptedTerms';
  private _autoRefreshTokensTimeoutId: number;
  private _decodedAccessToken$: BehaviorSubject<IDecodedAccessToken>;

  constructor(
    private http: HttpClient,
    private _router: Router,
    private urls: UrlConstantsService
  ) {
    this._autoRefreshTokens();
    this._decodedAccessToken$ = new BehaviorSubject<IDecodedAccessToken>(this._getDecodedAccessToken());
    this.decodedAccessToken$ = this._decodedAccessToken$.asObservable();
    this.userName$ = this.decodedAccessToken$.pipe(map((data) => data?.name ?? 'UNKNOWN'));
    if (this.isAuthenticated()) Sentry.setUser({ email: this._decodedAccessToken$.value?.name });
  }

  /** Your role in the organization. Tokens issued before roles existed default to member. */
  public orgRole(): 'owner' | 'admin' | 'member' {
    return this._getDecodedAccessToken()?.org_role ?? 'member';
  }

  public hasAdminPreference(): boolean {
    return !!this._getDecodedAccessToken()?.platform_admin;
  }

  public getUserId(): number {
    return this._getDecodedAccessToken()?.user_id;
  }

  public login(username, password): Observable<boolean> {
    return this.http
      .post<IAuthTokens>(this.urls.USER_LOGIN_EMAIL, { username: username, password })
      .pipe(
        tap((tokens) => {
          Sentry.setUser({ email: username });
          this.saveAuthToken(tokens);
        }),
        map((_) => true),
        catchError((_) => of(false))
      );
  }

  public refreshToken(): Observable<IAuthTokens> {
    return this.http
      .post<IAuthTokens>(this.urls.USER_REFRESH_TOKENS, { refresh: this.getRefreshToken() })
      .pipe(tap((tokens) => this.saveAuthToken(tokens)));
  }

  /**
   * Errors are passed on (not swallowed into `false`) so the register page can show the
   * server's field messages, e.g. an invalid invitation code.
   */
  /** Joins an organization through an invitation link and logs the new member in. */
  public acceptInvitation(token: string, formValue: { first_name: string; last_name: string; password: string }): Observable<boolean> {
    return this.http.post<IAuthTokens>(this.urls.INVITATION_ACCEPT(token), formValue).pipe(
      tap((tokens) => this.saveAuthToken(tokens)),
      map((_) => true),
    );
  }

  public registerUser(formValue: any): Observable<boolean> {
    return this.http
      .post<any>(this.urls.USER_REGISTER_EMAIL, formValue)
      .pipe(
        tap((tokens) => {
          Sentry.setUser({ email: formValue.email });
          this.saveAuthToken(tokens);
        }),
        map((_) => true),
      );
  }


  public getExpiration(): number {
    return this._getDecodedAccessToken()?.exp || null;
  }

  public isTokenExpired() {
    return this.getExpiration() < Date.now() / 1000;
  }

  public isAuthenticated(): boolean {
    console.log(this._getDecodedAccessToken())
    return !!this._getDecodedAccessToken()?.user_id;
  }

  public logout(keepAfterLoginPath: boolean = false): void {
    this.clearAuthToken();
    if (keepAfterLoginPath) this.setAfterLoginPath(this._router.url);
    this._router.navigate(['/']);
  }

  public getUserName(): string {
    return this._getDecodedAccessToken()?.name ?? 'UNKNOWN';
  }

  public getAccessToken(): string {
    return localStorage.getItem(this._accessToken);
  }

  public getRefreshToken(): string {
    return localStorage.getItem(this._refreshToken);
  }

  public isTermsAccepted(): boolean {
    return localStorage.getItem(this._acceptedTerms) === 'true';
  }

  public saveAuthToken(tokens: IAuthTokens): void {
    localStorage.setItem(this._accessToken, tokens.access);
    this._decodedAccessToken$.next(this._getDecodedAccessToken(tokens.access));
    console.log('tokens saved');
  }

  public clearAuthToken(): void {
    localStorage.removeItem(this._accessToken);
    localStorage.removeItem(this._refreshToken);
    this._decodedAccessToken$.next(this._getDecodedAccessToken(null));
  }

  public getAfterLoginPath(): string {
    return sessionStorage.getItem(this._afterLoginPath) || '';
  }

  public setAfterLoginPath(path: string): void {
    sessionStorage.setItem(this._afterLoginPath, path);
  }

  public deleteAfterLoginPath(): void {
    sessionStorage.removeItem(this._afterLoginPath);
  }

  private _getDecodedAccessToken(accessToken: string = this.getAccessToken()): IDecodedAccessToken {
    try {
      return JSON.parse(atob(accessToken.split('.')[1]));
    } catch {
      return null;
    }
  }

  private _autoRefreshTokens(): void {
    if (!this.isAuthenticated()) return;
    const expirationPeriod = this.getExpiration() * 1000 - Date.now();
    clearTimeout(this._autoRefreshTokensTimeoutId);
    this._autoRefreshTokensTimeoutId = setTimeout(() => this.refreshToken().subscribe(), expirationPeriod) as any;
  }
}
